import * as React from 'react';

import { Button } from '@playbooks/ui/components/button';
import { FarIcon } from '@playbooks/ui/components/icon';
import { cn } from '@playbooks/ui/lib/utils';

type TableProps = React.ComponentProps<'table'> & {
	density?: 'compact' | 'default' | 'comfortable';
};

function Table({ className, density = 'default', ...props }: TableProps) {
	// Theme
	const rootClassName = cn(
		'group/table w-full caption-bottom text-sm data-[density=compact]:text-xs data-[density=compact]:font-normal',
		className,
	);

	// Render
	return (
		<div data-slot='table-container' className='relative w-full overflow-x-auto'>
			<table data-slot='table' data-density={density} className={rootClassName} {...props} />
		</div>
	);
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
	// Theme
	const rootClassName = cn('[&_tr]:border-b', className);

	// Render
	return <thead data-slot='table-header' className={rootClassName} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
	// Theme
	const rootClassName = cn(
		'[&_tr]:h-11 [&_tr:last-child]:border-0 group-data-[density=compact]/table:[&_tr]:h-10 group-data-[density=comfortable]/table:[&_tr]:h-14',
		className,
	);

	// Render
	return <tbody data-slot='table-body' className={rootClassName} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
	// Theme
	const rootClassName = cn('border-t bg-surface font-medium', className);

	// Render
	return <tfoot data-slot='table-footer' className={rootClassName} {...props} />;
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
	// Theme
	const rootClassName = cn('border-b transition-colors data-[state=selected]:bg-surface-selected', className);

	// Render
	return <tr data-slot='table-row' className={rootClassName} {...props} />;
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
	// Theme
	const rootClassName = cn(
		'h-9 px-3 py-0 text-left align-middle text-xs font-medium whitespace-nowrap text-foreground-body group-data-[density=compact]/table:h-8 group-data-[density=comfortable]/table:h-10',
		className,
	);

	// Render
	return <th data-slot='table-head' className={rootClassName} {...props} />;
}

type SortParams = { sortProp?: string; sortValue?: 'asc' | 'desc'; [key: string]: unknown };
type SortableTableHeadProps = Omit<React.ComponentProps<'th'>, 'value'> & {
	value?: string;
	params?: SortParams;
	setParams?: (params: SortParams) => void;
	alt?: string;
};

function SortableTableHead({
	value,
	params,
	setParams,
	alt = 'Sort table',
	children,
	...props
}: SortableTableHeadProps) {
	const sortable = Boolean(value && params && setParams);
	const active = value === params?.sortProp;
	const direction = active && params?.sortValue === 'asc' ? 'desc' : 'asc';

	return (
		<TableHead {...props}>
			{sortable ? (
				<Button
					variant='ghost'
					size='xs'
					aria-label={alt}
					onClick={() => setParams?.({ ...params, sortProp: value, sortValue: direction })}
					className='-mx-2 h-7 justify-start px-2 py-0 text-left text-foreground-body hover:text-foreground'>
					{children}
					<FarIcon icon={active ? (params?.sortValue === 'asc' ? 'sort-up' : 'sort-down') : 'sort'} />
				</Button>
			) : (
				children
			)}
		</TableHead>
	);
}

type TableCellProps = React.ComponentProps<'td'> & { value?: React.ReactNode; title?: boolean };

function TableCell({ className, value, title, children, ...props }: TableCellProps) {
	// Theme
	const rootClassName = cn(
		'px-4 py-2.5 text-left align-middle text-foreground/85 whitespace-nowrap group-data-[density=compact]/table:px-3 group-data-[density=compact]/table:py-2 group-data-[density=compact]/table:font-normal group-data-[density=compact]/table:[&_a]:text-xs group-data-[density=compact]/table:[&_a]:font-normal group-data-[density=compact]/table:[&_[data-slot=button]]:text-xs group-data-[density=compact]/table:[&_[data-slot=button]]:font-normal group-data-[density=comfortable]/table:py-3',
		className,
	);

	// Render
	return (
		<td data-slot='table-cell' data-title={title || undefined} className={rootClassName} {...props}>
			{value ?? children}
		</td>
	);
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
	// Theme
	const rootClassName = cn('mt-4 text-sm text-foreground-body', className);

	// Render
	return <caption data-slot='table-caption' className={rootClassName} {...props} />;
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, SortableTableHead, TableRow, TableCell, TableCaption };
